/**
 * CodeMirror extension that makes `<!-- embed: component.md -->` directives
 * navigable in the source editor: the component name is styled as a link, and
 * double-clicking it (or Cmd/Ctrl+Click) opens the component. (A ↗ button used
 * to follow the directive, but the gesture alone keeps the UI cleaner.)
 */

import {
  Decoration,
  EditorView,
  ViewPlugin,
  type DecorationSet,
  type ViewUpdate,
} from '@codemirror/view';
import type { Extension, Range } from '@codemirror/state';

/** Matches a single-line embed directive; group 1 is the component name */
const EMBED_RE = /<!--\s*embed:\s*([^|>\n]+?)\s*(?:\|[^>\n]*?)?-->/g;
/** Prefix up to the start of the component name within a matched directive */
const EMBED_PREFIX_RE = /^<!--\s*embed:\s*/;

function buildDecorations(view: EditorView): DecorationSet {
  const decorations: Range<Decoration>[] = [];

  for (const { from, to } of view.visibleRanges) {
    const text = view.state.doc.sliceString(from, to);
    EMBED_RE.lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = EMBED_RE.exec(text)) !== null) {
      const name = match[1];
      const prefixLen = EMBED_PREFIX_RE.exec(match[0])![0].length;
      const nameStart = from + match.index + prefixLen;

      decorations.push(
        Decoration.mark({
          class: 'cm-embed-link',
          attributes: {
            'data-embed': name,
            title: `Double-click (or ${navigator.platform.includes('Mac') ? 'Cmd' : 'Ctrl'}+Click) to open ${name}`,
          },
        }).range(nameStart, nameStart + name.length)
      );
    }
  }

  return Decoration.set(decorations, true);
}

/**
 * Create the embed-links extension.
 * @param openComponent Called with the component name (as written in the
 *   directive, e.g. "docker-install.md") when the user activates a link.
 */
export function embedLinks(openComponent: (name: string) => void): Extension {
  return ViewPlugin.fromClass(
    class {
      decorations: DecorationSet;

      constructor(view: EditorView) {
        this.decorations = buildDecorations(view);
      }

      update(update: ViewUpdate): void {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildDecorations(update.view);
        }
      }
    },
    {
      decorations: (v) => v.decorations,
      eventHandlers: {
        mousedown(e: MouseEvent, _view: EditorView): boolean {
          const target = e.target as HTMLElement;
          const link = target.closest('.cm-embed-link') as HTMLElement | null;
          if (link?.dataset.embed && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            openComponent(link.dataset.embed);
            return true;
          }
          return false;
        },
        dblclick(e: MouseEvent, _view: EditorView): boolean {
          const target = e.target as HTMLElement;
          const link = target.closest('.cm-embed-link') as HTMLElement | null;
          if (link?.dataset.embed) {
            // Open instead of selecting the word under the cursor.
            e.preventDefault();
            openComponent(link.dataset.embed);
            return true;
          }
          return false;
        },
      },
    }
  );
}
